import { useVirtualizer } from "@tanstack/react-virtual";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import CommentsDisabledOutlinedIcon from "@mui/icons-material/CommentsDisabledOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, type FieldErrors, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as GABulkUpload from "~/app/analytics/ga/bulkUpload";
import { format as formatApiDate, getDate } from "~/common/utils/ApiHelper";
import {
  getPriceReferenceOptions,
  type PriceReferenceSelectOption,
} from "~/domains/priceReference/priceReferenceSelection";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  styled,
  Switch,
  TextField,
  Typography,
} from "~/common/mui";
import CheckMessagesComponent from "~/common/components/CheckMessagesComponent";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import { MUIDateInput } from "~/common/components/MUIDateInput";
import { getFirstValidDate, getLastValidDate } from "~/common/utils/dateUtils";
import type { ProductData, ProductMarketType } from "~/domains/product/types/ProductData";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import type { BulkUploadEntryIntentWsDTO, BulkUploadPreviewProduct, BulkUploadRowErrorWsDTO } from "../types/contracts";
import type { BulkUploadWorkflowFailure } from "../workflow/bulkUploadReducer";
import { BulkUploadLoading } from "./BulkUploadLoading";
import PreviewTableMaterialIdInput from "./PreviewTable/PreviewTableMaterialIdInput";

type EditableField =
  | "productId"
  | "quantity"
  | "requestDeliveryDate"
  | "comment"
  | "priceReference"
  | "customerOrderNumber"
  | "targetPrice";

type TrackedProductField = Extract<
  EditableField,
  "productId" | "quantity" | "requestDeliveryDate" | "priceReference" | "targetPrice"
>;

interface BulkUploadFormRow {
  productId: string;
  quantity: number | "";
  requestDeliveryDate: string;
  comment: string;
  priceReference: string;
  customerOrderNumber: string;
  targetPrice: number | "";
}

interface BulkUploadForm {
  rows: BulkUploadFormRow[];
}

interface RowMetadata {
  housingProduct: boolean;
  isPMA: boolean;
  marketType: ProductMarketType | null;
  messages: BulkUploadPreviewProduct["messages"];
  onSale: boolean;
  quantityVisible: boolean;
  originalProductId: string;
}

interface BackendError {
  code: string;
  field: EditableField;
  fieldId: string;
}

interface ErrorStop extends BackendError {
  actualIndex: number;
  focusKey: string;
  key: string;
  visibleIndex: number;
}

export interface BulkUploadPreviewProps {
  bulkMaxRows?: string;
  customerOrderNumberEnabled: boolean;
  disabled: boolean;
  failure?: BulkUploadWorkflowFailure;
  priceReferenceSelection?: PriceReferenceSelection;
  onCancel: () => void;
  onLastRowRemoved: () => void;
  onOverrideConfirmationClose: () => void;
  onOverrideConfirmationOpen: () => void;
  /** Optional observability callback for deterministic row-render isolation checks. */
  onRowRender?: (rowId: string) => void;
  onSaleOnlyChange: (value: boolean) => void;
  onTargetPricesChange?: (targetPrices: Array<number | null>) => void;
  onSubmit: (entries: BulkUploadEntryIntentWsDTO[], override: boolean) => void;
  overrideConfirmationOpen: boolean;
  pending: boolean;
  products: BulkUploadPreviewProduct[];
  rowErrors: BulkUploadRowErrorWsDTO[];
  saleOnly: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  showOverrideAction: boolean;
  targetPriceEnabled: boolean;
  terminalActionsDisabled?: boolean;
}

const PREVIEW_ROW_ESTIMATE = 220;
const ERROR_SCROLL_ANCHOR = 96;

export function BulkUploadPreview({
  bulkMaxRows,
  customerOrderNumberEnabled,
  disabled,
  failure,
  priceReferenceSelection,
  onCancel,
  onLastRowRemoved,
  onOverrideConfirmationClose,
  onOverrideConfirmationOpen,
  onRowRender,
  onSaleOnlyChange,
  onTargetPricesChange,
  onSubmit,
  overrideConfirmationOpen,
  pending,
  products,
  rowErrors,
  saleOnly,
  scrollContainerRef,
  showOverrideAction,
  targetPriceEnabled,
  terminalActionsDisabled = false,
}: BulkUploadPreviewProps) {
  const { t } = useTranslation();
  const form = useForm<BulkUploadForm>({
    defaultValues: {
      rows: products.map(toFormRow),
    },
    mode: "onChange",
    shouldUnregister: false,
  });
  const { fields, remove } = useFieldArray({
    control: form.control,
    keyName: "fieldId",
    name: "rows",
  });
  const metadataById = useRef(new Map<string, RowMetadata>());
  const trackedFieldValues = useRef(new Map<string, BulkUploadFormRow[TrackedProductField]>());
  const [messageRevision, setMessageRevision] = useState(0);
  const [allCommentsOpen, setAllCommentsOpen] = useState(false);

  fields.forEach((field, index) => {
    if (!metadataById.current.has(field.fieldId)) {
      const source = products[index];
      metadataById.current.set(field.fieldId, {
        housingProduct: source?.housingProduct ?? false,
        isPMA: source?.isPMA ?? false,
        marketType: source?.type ?? null,
        messages: source?.messages ?? [],
        onSale: source?.onSale ?? false,
        originalProductId: source?.productId ?? field.productId,
        quantityVisible: source?.quantityVisible ?? true,
      });
    }
  });

  const indexedFields = useMemo(() => {
    void messageRevision;
    return fields.map((field, actualIndex) => ({
      actualIndex,
      field,
      metadata: metadataById.current.get(field.fieldId)!,
    }));
  }, [fields, messageRevision]);
  const visibleFields = useMemo(
    () => indexedFields.filter(({ metadata }) => !saleOnly || metadata.onSale),
    [indexedFields, saleOnly],
  );
  const priceReferenceOptions = priceReferenceSelection ? getPriceReferenceOptions(priceReferenceSelection, false) : [];
  // Sale items use the Price reference slot to keep the sale tag aligned with
  // the corresponding header instead of placing it below the Product ID.
  const showPriceReferenceColumn =
    priceReferenceOptions.length > 0 || visibleFields.some(({ metadata }) => metadata.onSale);
  const showTargetPriceColumn = visibleFields.some(({ metadata }) =>
    isTargetPriceApplicable(targetPriceEnabled, metadata),
  );
  const fieldGenerationKey = `${fields.length}:${fields[0]?.fieldId ?? ""}:${fields[fields.length - 1]?.fieldId ?? ""}`;

  const [backendErrors, setBackendErrors] = useState<BackendError[]>([]);
  const lastProductsResponse = useRef(products);
  const pendingResponseMappingGeneration = useRef<string>();
  const lastMappedResponse = useRef<{
    products: BulkUploadPreviewProduct[];
    rowErrors: BulkUploadRowErrorWsDTO[];
  }>();

  useEffect(() => {
    if (lastProductsResponse.current === products) {
      return;
    }
    lastProductsResponse.current = products;
    trackedFieldValues.current.clear();
    pendingResponseMappingGeneration.current = fieldGenerationKey;
    metadataById.current.clear();
    setMessageRevision((revision) => revision + 1);
    setBackendErrors([]);
    form.reset({
      rows: products.map(toFormRow),
    });
  }, [fieldGenerationKey, form, products]);

  useEffect(() => {
    if (lastMappedResponse.current?.products === products && lastMappedResponse.current.rowErrors === rowErrors) {
      return;
    }
    if (pendingResponseMappingGeneration.current === fieldGenerationKey) {
      return;
    }
    pendingResponseMappingGeneration.current = undefined;
    lastMappedResponse.current = {
      products,
      rowErrors,
    };
    const mapped: BackendError[] = [];
    rowErrors.forEach((error) => {
      const row = indexedFields[error.rowNumber - 1];
      if (row) {
        mapped.push({
          code: error.code,
          field: normalizeEditableField(error.field),
          fieldId: row.field.fieldId,
        });
      }
    });

    products.forEach((product, index) => {
      const row = indexedFields[index];
      if (!row) {
        return;
      }
      product.messages
        .filter((message) => message.type === "error")
        .forEach((message) => {
          mapped.push({
            code: message.code,
            field: inferMessageField(message.code),
            fieldId: row.field.fieldId,
          });
        });
    });

    setBackendErrors(dedupeBackendErrors(mapped));
  }, [fieldGenerationKey, indexedFields, products, rowErrors]);

  const clearFieldErrors = useCallback(
    (fieldId: string, actualIndex: number, field: EditableField) => {
      setBackendErrors((current) => {
        const next = current.filter((error) => error.fieldId !== fieldId || error.field !== field);
        return next.length === current.length ? current : next;
      });
      const metadata = metadataById.current.get(fieldId);
      if (metadata) {
        const messages = metadata.messages.filter((message) => getFieldForMessage(message.code) !== field);
        if (messages.length !== metadata.messages.length) {
          metadataById.current.set(fieldId, { ...metadata, messages });
          setMessageRevision((revision) => revision + 1);
        }
      }
      form.clearErrors(`rows.${actualIndex}.${field}`);
    },
    [form],
  );
  const validateTargetPrice = useCallback(
    (actualIndex: number) => {
      const value = form.getValues(`rows.${actualIndex}.targetPrice`);
      if (value === "") {
        form.clearErrors(`rows.${actualIndex}.targetPrice`);
        return;
      }
      const targetPrice = Number(value);
      if (!Number.isFinite(targetPrice) || targetPrice <= 0) {
        form.setError(`rows.${actualIndex}.targetPrice`, {
          message: "bulkUpload.error.targetPrice.invalid",
          type: "min",
        });
      } else {
        form.clearErrors(`rows.${actualIndex}.targetPrice`);
      }
    },
    [form],
  );
  const backendErrorsByFieldId = useMemo(() => {
    const grouped = new Map<string, BackendError[]>();
    backendErrors.forEach((error) => {
      const rowErrors = grouped.get(error.fieldId);
      if (rowErrors) {
        rowErrors.push(error);
      } else {
        grouped.set(error.fieldId, [error]);
      }
    });
    return grouped;
  }, [backendErrors]);

  const rhfErrors = form.formState.errors;
  const currentRows = useWatch({ control: form.control, name: "rows" });
  useEffect(() => {
    onTargetPricesChange?.(
      currentRows.map(({ targetPrice }) =>
        typeof targetPrice === "number" && Number.isFinite(targetPrice) ? targetPrice : null,
      ),
    );
  }, [currentRows, onTargetPricesChange]);
  const errorStops = useMemo(
    () =>
      buildErrorStops({
        backendErrors,
        errors: rhfErrors,
        visibleFields,
      }),
    [backendErrors, rhfErrors, visibleFields],
  );
  const hasInvalidVisibleValues = visibleFields.some(({ actualIndex, metadata }) => {
    const row = currentRows[actualIndex];
    if (!row || !row.productId.trim() || !Number.isFinite(Number(row.quantity)) || Number(row.quantity) < 1) {
      return true;
    }
    if (isTargetPriceApplicable(targetPriceEnabled, metadata) && row.targetPrice !== "") {
      const targetPrice = Number(row.targetPrice);
      return !Number.isFinite(targetPrice) || targetPrice <= 0;
    }
    return false;
  });
  const terminalValidationBlocked = errorStops.length > 0 || hasInvalidVisibleValues;
  const infoStops = useMemo(
    () =>
      visibleFields.flatMap(({ metadata }, visibleIndex) =>
        metadata.messages
          .filter((message) => message.type === "info" && !getFieldForMessage(message.code))
          .map(() => ({ visibleIndex })),
      ),
    [visibleFields],
  );
  const [activeErrorKey, setActiveErrorKey] = useState<string>();
  const activeErrorOrdinal = Math.max(
    0,
    errorStops.findIndex((error) => error.key === activeErrorKey),
  );
  const activeErrorFocusKey = errorStops[activeErrorOrdinal]?.focusKey;
  const activeErrorIndex = useRef(0);
  const [pendingFocusKey, setPendingFocusKey] = useState<string>();
  const pendingFocusKeyRef = useRef<string>();
  const programmaticTargetIndexRef = useRef<number>();
  const submitOverrideRef = useRef(false);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [manualScrollOffset, setManualScrollOffset] = useState<number>();
  const [forcedVisibleIndex, setForcedVisibleIndex] = useState<number>();

  useEffect(() => {
    const inapplicableFieldIds = new Set(
      indexedFields
        .filter(({ metadata }) => !isTargetPriceApplicable(targetPriceEnabled, metadata))
        .map(({ actualIndex, field }) => {
          form.clearErrors(`rows.${actualIndex}.targetPrice`);
          return field.fieldId;
        }),
    );
    setBackendErrors((current) => {
      const next = current.filter((error) => error.field !== "targetPrice" || !inapplicableFieldIds.has(error.fieldId));
      return next.length === current.length ? current : next;
    });
  }, [form, indexedFields, targetPriceEnabled]);

  useEffect(() => {
    setScrollElement(scrollContainerRef.current);
  }, [scrollContainerRef]);

  useEffect(() => {
    if (errorStops.length === 0) {
      setActiveErrorKey(undefined);
      activeErrorIndex.current = 0;
      return;
    }
    const current = errorStops.find((error) => error.key === activeErrorKey);
    if (current) {
      activeErrorIndex.current = current.actualIndex;
      return;
    }
    const next =
      errorStops.find((error) => error.actualIndex >= activeErrorIndex.current) ?? errorStops[errorStops.length - 1];
    programmaticTargetIndexRef.current = undefined;
    setForcedVisibleIndex(undefined);
    activeErrorIndex.current = next.actualIndex;
    setActiveErrorKey(next.key);
  }, [activeErrorKey, errorStops]);

  const rowVirtualizer = useVirtualizer({
    count: visibleFields.length,
    estimateSize: () => PREVIEW_ROW_ESTIMATE,
    getItemKey: (index) => visibleFields[index]?.field.fieldId ?? index,
    getScrollElement: () => scrollElement,
    initialRect: {
      height: 600,
      width: 1200,
    },
    observeElementRect: (instance, callback) => {
      const element = instance.scrollElement;
      if (!element) {
        return;
      }
      const updateRect = () =>
        callback({
          height: element.clientHeight || 600,
          width: element.clientWidth || 1200,
        });
      updateRect();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(updateRect);
      observer.observe(element);
      return () => observer.disconnect();
    },
    observeElementOffset: (instance, callback) => {
      const element = instance.scrollElement;
      if (!element) {
        return;
      }
      const updateOffset = () => callback(element.scrollTop, true);
      callback(element.scrollTop, false);
      element.addEventListener("scroll", updateOffset, { passive: true });
      return () => element.removeEventListener("scroll", updateOffset);
    },
    overscan: 6,
    rangeExtractor: ({ count, endIndex, overscan, startIndex }) => {
      const start = Math.max(0, startIndex - overscan);
      const end = Math.min(count - 1, endIndex + overscan);
      const indexes = Array.from({ length: end - start + 1 }, (_, index) => start + index);
      if (
        forcedVisibleIndex !== undefined &&
        forcedVisibleIndex >= 0 &&
        forcedVisibleIndex < count &&
        !indexes.includes(forcedVisibleIndex)
      ) {
        indexes.push(forcedVisibleIndex);
        indexes.sort((left, right) => left - right);
      }
      return indexes;
    },
    scrollPaddingEnd: 96,
    scrollPaddingStart: 96,
    scrollToFn: (offset, { adjustments = 0 }, instance) => {
      const element = instance.scrollElement;
      if (!element) {
        return;
      }
      element.scrollTop = offset + adjustments;
      element.dispatchEvent(new Event("scroll"));
    },
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualRangeKey = virtualItems.map((item) => item.key).join("|");
  const naturalRangeStart = rowVirtualizer.range?.startIndex;
  const naturalRangeEnd = rowVirtualizer.range?.endIndex;
  const measureVirtualRow = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        window.setTimeout(() => {
          if (element.getBoundingClientRect().height > 0) {
            rowVirtualizer.measureElement(element);
          }
        }, 0);
      }
    },
    [rowVirtualizer],
  );

  const focusInfo = useCallback(
    (index: number) => {
      const stop = infoStops[index];
      const scrollContainer = scrollContainerRef.current;
      if (!stop || !scrollContainer) {
        return;
      }
      const offset =
        rowVirtualizer.getOffsetForIndex(stop.visibleIndex, "center")?.[0] ?? stop.visibleIndex * PREVIEW_ROW_ESTIMATE;
      scrollContainer.scrollTop = offset;
      scrollContainer.dispatchEvent(new Event("scroll"));
    },
    [infoStops, rowVirtualizer, scrollContainerRef],
  );

  const focusError = useCallback(
    (error: ErrorStop) => {
      activeErrorIndex.current = error.actualIndex;
      setActiveErrorKey(error.key);
      pendingFocusKeyRef.current = error.focusKey;
      programmaticTargetIndexRef.current = error.visibleIndex;
      setPendingFocusKey(error.focusKey);
      setForcedVisibleIndex(error.visibleIndex);
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const offset =
          rowVirtualizer.getOffsetForIndex(error.visibleIndex, "center")?.[0] ??
          error.visibleIndex * PREVIEW_ROW_ESTIMATE;
        scrollContainer.scrollTop = offset;
        scrollContainer.dispatchEvent(new Event("scroll"));
      }
    },
    [rowVirtualizer, scrollContainerRef],
  );

  const navigateError = useCallback(
    (direction: -1 | 1) => {
      if (errorStops.length === 0) {
        return;
      }
      const currentIndex = Math.max(
        0,
        errorStops.findIndex((error) => error.key === activeErrorKey),
      );
      const nextIndex = Math.min(errorStops.length - 1, Math.max(0, currentIndex + direction));
      focusError(errorStops[nextIndex]);
    },
    [activeErrorKey, errorStops, focusError],
  );

  useEffect(() => {
    if (!pendingFocusKey) {
      return;
    }
    const root = scrollContainerRef.current;
    const focusTarget = Array.from(root?.querySelectorAll<HTMLElement>("[data-error-focus]") ?? []).find(
      (element) => element.dataset.errorFocus === pendingFocusKey,
    );
    if (focusTarget) {
      const input = focusTarget.matches("input, textarea, button, [tabindex]")
        ? focusTarget
        : focusTarget.querySelector<HTMLElement>("input, textarea, button, [tabindex]");
      if (input) {
        input.focus();
        pendingFocusKeyRef.current = undefined;
        setPendingFocusKey(undefined);
      }
    }
  }, [pendingFocusKey, scrollContainerRef, virtualRangeKey]);

  useEffect(() => {
    if (
      forcedVisibleIndex !== undefined &&
      !pendingFocusKey &&
      naturalRangeStart !== undefined &&
      naturalRangeEnd !== undefined &&
      forcedVisibleIndex >= naturalRangeStart &&
      forcedVisibleIndex <= naturalRangeEnd
    ) {
      programmaticTargetIndexRef.current = undefined;
      setForcedVisibleIndex(undefined);
    }
  }, [forcedVisibleIndex, naturalRangeEnd, naturalRangeStart, pendingFocusKey]);

  useEffect(() => {
    if (pendingFocusKey && !errorStops.some((error) => error.focusKey === pendingFocusKey)) {
      pendingFocusKeyRef.current = undefined;
      programmaticTargetIndexRef.current = undefined;
      setPendingFocusKey(undefined);
      setForcedVisibleIndex(undefined);
    }
  }, [errorStops, pendingFocusKey]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }
    const captureManualScroll = () => {
      if (pendingFocusKeyRef.current || programmaticTargetIndexRef.current !== undefined) {
        return;
      }
      setManualScrollOffset(scrollElement.scrollTop);
    };
    scrollElement.addEventListener("scroll", captureManualScroll, { passive: true });
    return () => scrollElement.removeEventListener("scroll", captureManualScroll);
  }, [scrollElement]);

  useEffect(() => {
    if (manualScrollOffset === undefined || errorStops.length === 0) {
      return;
    }
    const contentAnchor = manualScrollOffset + ERROR_SCROLL_ANCHOR;
    const nearest = errorStops.reduce((best, candidate) => {
      const candidateOffset =
        rowVirtualizer.getOffsetForIndex(candidate.visibleIndex, "start")?.[0] ??
        candidate.visibleIndex * PREVIEW_ROW_ESTIMATE;
      const bestOffset =
        rowVirtualizer.getOffsetForIndex(best.visibleIndex, "start")?.[0] ?? best.visibleIndex * PREVIEW_ROW_ESTIMATE;
      return Math.abs(candidateOffset - contentAnchor) < Math.abs(bestOffset - contentAnchor) ? candidate : best;
    });
    activeErrorIndex.current = nearest.actualIndex;
    setActiveErrorKey(nearest.key);
    setManualScrollOffset(undefined);
  }, [errorStops, manualScrollOffset, naturalRangeEnd, naturalRangeStart, rowVirtualizer]);

  const trackFieldInteraction = useCallback(
    (
      fieldId: string,
      actualIndex: number,
      field: TrackedProductField,
      previousValue: BulkUploadFormRow[TrackedProductField],
      value: BulkUploadFormRow[TrackedProductField],
    ) => {
      const previousRow = form.getValues(`rows.${actualIndex}`);
      const trackedValueKey = `${fieldId}:${field}`;
      const trackedPreviousValue = trackedFieldValues.current.has(trackedValueKey)
        ? trackedFieldValues.current.get(trackedValueKey)!
        : previousValue;
      if (Object.is(trackedPreviousValue, value)) {
        return;
      }
      const updatedRow = {
        ...previousRow,
        [field]: value,
      };
      trackedFieldValues.current.set(trackedValueKey, value);
      const previousItem = toAnalyticsPreviewItem({ ...updatedRow, [field]: trackedPreviousValue });
      const updatedItem = toAnalyticsPreviewItem(updatedRow);
      queueMicrotask(() => {
        GABulkUpload.trackProductInteraction({
          field,
          previousItem,
          successful: true,
          updatedItem,
        });
      });
    },
    [form],
  );

  const removeRow = useCallback(
    (fieldId: string, actualIndex: number) => {
      const removedItem = toAnalyticsPreviewItem(form.getValues(`rows.${actualIndex}`));
      metadataById.current.delete(fieldId);
      setBackendErrors((current) => current.filter((error) => error.fieldId !== fieldId));
      if (fields.length === 1) {
        onLastRowRemoved();
        GABulkUpload.trackProductRemoved({ removedItem, successful: true });
        return;
      }
      remove(actualIndex);
      GABulkUpload.trackProductRemoved({ removedItem, successful: true });
      const remainingSaleRows = indexedFields.filter(
        ({ field, metadata }) => field.fieldId !== fieldId && metadata.onSale,
      );
      if (saleOnly && remainingSaleRows.length === 0) {
        onSaleOnlyChange(false);
      }
    },
    [fields.length, form, indexedFields, onLastRowRemoved, onSaleOnlyChange, remove, saleOnly],
  );

  const removeAllRows = useCallback(() => {
    fields.forEach((field, actualIndex) => {
      GABulkUpload.trackProductRemoved({
        removedItem: toAnalyticsPreviewItem(form.getValues(`rows.${actualIndex}`)),
        successful: true,
      });
    });
    metadataById.current.clear();
    setBackendErrors([]);
    onLastRowRemoved();
  }, [fields, form, onLastRowRemoved]);

  const validateAndSubmit = useCallback(
    (values: BulkUploadForm, override: boolean) => {
      form.clearErrors();
      let hasClientErrors = false;

      visibleFields.forEach(({ actualIndex, metadata }) => {
        const row = values.rows[actualIndex];
        if (!row.productId.trim()) {
          form.setError(`rows.${actualIndex}.productId`, {
            message: "bulkUpload.error.product.id.invalid",
            type: "required",
          });
          hasClientErrors = true;
        }
        const quantity = Number(row.quantity);
        if (!Number.isFinite(quantity) || quantity < 1) {
          form.setError(`rows.${actualIndex}.quantity`, {
            message: "bulkUpload.error.product.quantity.invalid",
            type: "min",
          });
          hasClientErrors = true;
        }
        if (isTargetPriceApplicable(targetPriceEnabled, metadata) && row.targetPrice !== "") {
          const targetPrice = Number(row.targetPrice);
          if (!Number.isFinite(targetPrice) || targetPrice <= 0) {
            form.setError(`rows.${actualIndex}.targetPrice`, {
              message: "bulkUpload.error.targetPrice.invalid",
              type: "min",
            });
            hasClientErrors = true;
          }
        }
      });

      const visibleFieldIds = new Set(visibleFields.map(({ field }) => field.fieldId));
      const hasBackendErrors = backendErrors.some((error) => visibleFieldIds.has(error.fieldId));
      if (hasClientErrors || hasBackendErrors) {
        const first = buildErrorStops({
          backendErrors,
          errors: form.formState.errors,
          visibleFields,
        })[0];
        if (first) {
          focusError(first);
        }
        return;
      }

      onSubmit(
        visibleFields.map(({ actualIndex, metadata }) =>
          toIntent(values.rows[actualIndex], metadata, targetPriceEnabled),
        ),
        override,
      );
    },
    [backendErrors, focusError, form, onSubmit, targetPriceEnabled, visibleFields],
  );
  const submitForm = form.handleSubmit((values) => {
    const override = submitOverrideRef.current;
    submitOverrideRef.current = false;
    validateAndSubmit(values, override);
  });
  const confirmOverride = () => {
    submitOverrideRef.current = true;
    void submitForm();
  };

  const saleCount = indexedFields.filter(({ metadata }) => metadata.onSale).length;
  const failureLabel =
    failure?.error?.reason ??
    (failure && (failure.kind !== "validation" || !failure.rowErrors?.length)
      ? "bulkUpload.error.webservice"
      : undefined);

  return (
    <>
      {failureLabel && (
        <Alert data-testid="bulk-upload-preview-failure" severity="error">
          {t(failureLabel, {
            bulkMaxRows,
            count: failure?.error?.params.length ?? 0,
            param: failure?.error?.params.join(", ") ?? "",
          })}
        </Alert>
      )}
      <Stack gap={1} mb={2}>
        {saleCount > 0 && (
          <Stack data-testid="bulk-upload-sale-only" direction="row" gap={1} alignItems="center">
            <Switch
              checked={saleOnly}
              inputProps={{ "aria-label": t("bulkentry.sale.only.toggle", { count: saleCount }) }}
              onChange={(event) => onSaleOnlyChange(event.target.checked)}
            />
            <Typography color="textSecondary" variant="bodyLarge">
              {t("bulkentry.sale.only.toggle", { count: saleCount })}
            </Typography>
            <SaleTag size={20} />
          </Stack>
        )}
        <Typography variant="bodyMedium">
          {t("bulkUpload.upload.success.new", { count: visibleFields.length })}
        </Typography>
      </Stack>
      {(infoStops.length > 0 || errorStops.length > 0) && (
        <StickyMessageSummary data-testid="bulk-upload-message-summary" gap={1}>
          {infoStops.length > 0 && (
            <MessageNavigation>
              <CheckMessagesComponent
                isBulkUploadPreview
                msgCount={infoStops.length}
                msgType="info"
                onScrollToMessage={focusInfo}
              />
            </MessageNavigation>
          )}
          {errorStops.length > 0 && (
            <ErrorNavigation severity="error">
              <Typography data-testid="bulk-upload-error-count" variant="bodyMedium">
                {activeErrorOrdinal + 1}/{errorStops.length} {t("component.checkMessages.error")}
              </Typography>
              <NavigationButtons>
                <IconButton
                  type="button"
                  aria-label={t("generic.next")}
                  data-testid="bulk-upload-error-next"
                  color="error"
                  size="small"
                  sx={{ p: 0 }}
                  onClick={() => navigateError(1)}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
                <IconButton
                  type="button"
                  aria-label={t("generic.back")}
                  data-testid="bulk-upload-error-previous"
                  color="error"
                  size="small"
                  sx={{ p: 0 }}
                  onClick={() => navigateError(-1)}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
              </NavigationButtons>
            </ErrorNavigation>
          )}
        </StickyMessageSummary>
      )}

      <PreviewForm noValidate data-testid="bulk-upload-preview" onSubmit={(event) => void submitForm(event)}>
        <Stack gap={2}>
          <TableHeader
            $showPriceReferenceColumn={showPriceReferenceColumn}
            $showTargetPriceColumn={showTargetPriceColumn}
            data-testid="bulk-upload-table-header"
          >
            <Typography variant="bodyMedium">{t("wishlistPage.table.header.number")}</Typography>
            <Typography variant="bodyMedium">{t("bulkUpload.table.header.productId")}</Typography>
            <Typography variant="bodyMedium">{t("cartPage.table.header.quantity.title")}</Typography>
            <Typography variant="bodyMedium">{t("cartPage.table.header.delivery.title")}</Typography>
            {showPriceReferenceColumn && (
              <Typography data-testid="bulk-upload-price-reference-header" variant="bodyMedium">
                {t("cartPage.table.header.priceReference.title")}
              </Typography>
            )}
            {showTargetPriceColumn && (
              <Typography data-testid="bulk-upload-target-price-header" variant="bodyMedium">
                {t("cartPage.table.header.targetPrice.title")}
              </Typography>
            )}
            <HeaderActions>
              {visibleFields.some(({ metadata }) => canUseCommentsForProduct(metadata.marketType)) && (
                <IconButton
                  type="button"
                  aria-label={t("bulkUpload.table.comments.toggleAll", { defaultValue: "Show or hide all comments" })}
                  aria-expanded={allCommentsOpen}
                  data-testid="bulk-upload-toggle-all-comments"
                  color="primary"
                  size="small"
                  onClick={() => setAllCommentsOpen((open) => !open)}
                >
                  {allCommentsOpen ? <CommentsDisabledOutlinedIcon /> : <CommentOutlinedIcon />}
                </IconButton>
              )}
              <IconButton
                type="button"
                aria-label={t("bulkUpload.table.deleteAll", { defaultValue: "Delete all line items" })}
                data-testid="bulk-upload-remove-all"
                color="primary"
                size="small"
                onClick={removeAllRows}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </HeaderActions>
          </TableHeader>

          <VirtualCanvas data-testid="bulk-upload-virtual-canvas" style={{ height: rowVirtualizer.getTotalSize() }}>
            {virtualItems.map((virtualRow) => {
              const row = visibleFields[virtualRow.index];
              if (!row) {
                return null;
              }
              return (
                <VirtualRow
                  key={row.field.fieldId}
                  ref={measureVirtualRow}
                  data-index={virtualRow.index}
                  data-field-id={row.field.fieldId}
                  data-testid="bulk-upload-virtual-row"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <BulkUploadPreviewRow
                    activeErrorKey={
                      activeErrorFocusKey?.startsWith(`${row.field.fieldId}:`) ? activeErrorFocusKey : undefined
                    }
                    allCommentsOpen={allCommentsOpen}
                    actualIndex={row.actualIndex}
                    backendErrors={backendErrorsByFieldId.get(row.field.fieldId) ?? EMPTY_BACKEND_ERRORS}
                    clearFieldErrors={clearFieldErrors}
                    control={form.control}
                    customerOrderNumberEnabled={customerOrderNumberEnabled}
                    fieldId={row.field.fieldId}
                    metadata={row.metadata}
                    onFieldInteraction={trackFieldInteraction}
                    onRemove={removeRow}
                    onRowRender={onRowRender}
                    priceReferenceOptions={priceReferenceOptions}
                    rowErrors={rhfErrors.rows?.[row.actualIndex]}
                    showPriceReferenceColumn={showPriceReferenceColumn}
                    showTargetPriceColumn={showTargetPriceColumn}
                    targetPriceEnabled={targetPriceEnabled}
                    onTargetPriceBlur={validateTargetPrice}
                    visibleIndex={virtualRow.index}
                  />
                </VirtualRow>
              );
            })}
          </VirtualCanvas>

          {visibleFields.length === 0 && <Alert severity="warning">{t("bulkUpload.error.empty")}</Alert>}

          <FooterActions disableSpacing>
            <Button
              type="button"
              data-testid="bulk-upload-btn-cancel"
              disabled={terminalActionsDisabled}
              variant="text"
              onClick={onCancel}
            >
              {t("bulkUpload.buttons.cancel")}
            </Button>
            {showOverrideAction && (
              <Button
                type="button"
                data-testid="bulk-upload-btn-override-cart"
                color="secondary"
                disabled={
                  terminalActionsDisabled ||
                  disabled ||
                  pending ||
                  terminalValidationBlocked ||
                  visibleFields.length === 0
                }
                onClick={onOverrideConfirmationOpen}
              >
                {t("bulkUpload.buttons.override")}
              </Button>
            )}
            <Button
              type="submit"
              data-testid="bulk-upload-btn-add-to-cart"
              disabled={
                terminalActionsDisabled ||
                disabled ||
                pending ||
                terminalValidationBlocked ||
                visibleFields.length === 0 ||
                fields.length === 0
              }
              onClick={() => {
                submitOverrideRef.current = false;
              }}
            >
              {t("bulkUpload.buttons.add")}
            </Button>
          </FooterActions>
        </Stack>
      </PreviewForm>

      {pending && (
        <LoadingOverlay open>
          <BulkUploadLoading operation="add" />
        </LoadingOverlay>
      )}

      {overrideConfirmationOpen && (
        <Dialog
          open
          data-testid="bulk-upload-override-confirmation"
          maxWidth="desktop-s"
          onClose={(_, reason) => {
            if (reason !== "backdropClick") {
              onOverrideConfirmationClose();
            }
          }}
        >
          <DialogContent>
            <Stack gap={2}>
              <Typography variant="headlineSmall">{t("bulkUpload.buttons.override")}</Typography>
              <Typography variant="bodyMedium">{t("deleteConfirmation.cart")}</Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              data-testid="bulk-upload-override-cancel"
              variant="text"
              onClick={onOverrideConfirmationClose}
            >
              {t("bulkUpload.buttons.cancel")}
            </Button>
            <Button
              type="button"
              data-testid="bulk-upload-override-confirm"
              disabled={disabled || pending}
              onClick={confirmOverride}
            >
              {t("bulkUpload.buttons.override")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

interface BulkUploadPreviewRowProps {
  allCommentsOpen: boolean;
  activeErrorKey?: string;
  actualIndex: number;
  backendErrors: BackendError[];
  clearFieldErrors: (fieldId: string, actualIndex: number, field: EditableField) => void;
  control: ReturnType<typeof useForm<BulkUploadForm>>["control"];
  customerOrderNumberEnabled: boolean;
  fieldId: string;
  metadata: RowMetadata;
  onRemove: (fieldId: string, actualIndex: number) => void;
  onRowRender?: (rowId: string) => void;
  priceReferenceOptions: PriceReferenceSelectOption[];
  rowErrors?: FieldErrors<BulkUploadFormRow>;
  showPriceReferenceColumn: boolean;
  showTargetPriceColumn: boolean;
  targetPriceEnabled: boolean;
  visibleIndex: number;
  onTargetPriceBlur: (actualIndex: number) => void;
  onFieldInteraction: (
    fieldId: string,
    actualIndex: number,
    field: TrackedProductField,
    previousValue: BulkUploadFormRow[TrackedProductField],
    value: BulkUploadFormRow[TrackedProductField],
  ) => void;
}

const BulkUploadPreviewRow = memo(function BulkUploadPreviewRow({
  activeErrorKey,
  allCommentsOpen,
  actualIndex,
  backendErrors,
  clearFieldErrors,
  control,
  customerOrderNumberEnabled,
  fieldId,
  metadata,
  onFieldInteraction,
  onRemove,
  onRowRender,
  priceReferenceOptions,
  rowErrors,
  showPriceReferenceColumn,
  showTargetPriceColumn,
  targetPriceEnabled,
  onTargetPriceBlur,
  visibleIndex,
}: BulkUploadPreviewRowProps) {
  const { t } = useTranslation();
  const getError = (field: EditableField) => {
    const backend = backendErrors.find((error) => error.field === field);
    const client = rowErrors?.[field];
    const message = metadata.messages.find((message) => getFieldForMessage(message.code) === field);
    return backend?.code ?? (typeof client?.message === "string" ? client.message : undefined) ?? message?.code;
  };
  const focusKey = (field: EditableField) => `${fieldId}:${field}:${getError(field) ?? ""}`;
  const rowMessages = dedupeRenderableMessages([
    ...metadata.messages
      .filter((message) => !getFieldForMessage(message.code))
      .map((message) => ({
        code: message.code,
        type: message.type,
      })),
  ]);
  useEffect(() => {
    onRowRender?.(metadata.originalProductId);
  });
  const [commentOpen, setCommentOpen] = useState(false);
  const canMakeComments = canUseCommentsForProduct(metadata.marketType);

  useEffect(() => {
    setCommentOpen(allCommentsOpen);
  }, [allCommentsOpen]);

  return (
    <RowCard
      $showPriceReferenceColumn={showPriceReferenceColumn}
      $showTargetPriceColumn={showTargetPriceColumn}
      data-testid={`bulk-upload-row-grid-${metadata.originalProductId}`}
    >
      <Typography variant="bodyMedium">{visibleIndex + 1}</Typography>
      <Stack gap={1}>
        <Controller
          control={control}
          name={`rows.${actualIndex}.productId`}
          render={({ field }) => {
            const error = getError("productId");
            return (
              <Box
                data-error-focus={activeErrorKey === focusKey("productId") ? activeErrorKey : undefined}
                data-testid={`bulk-upload-product-${metadata.originalProductId}`}
              >
                <PreviewTableMaterialIdInput
                  disabled={false}
                  error={!!error}
                  helperText={error ? t(error) : undefined}
                  label={t("bulkUpload.table.header.productId")}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={(product: ProductData, inputValue: string) => {
                    clearFieldErrors(fieldId, actualIndex, "productId");
                    const value = resolveProductId(product, inputValue);
                    const previousValue = field.value;
                    field.onChange(value);
                    onFieldInteraction(fieldId, actualIndex, "productId", previousValue, value);
                  }}
                />
              </Box>
            );
          }}
        />
        {customerOrderNumberEnabled && (
          <Controller
            control={control}
            name={`rows.${actualIndex}.customerOrderNumber`}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t("search.type.customerOrderNumber")}
                inputProps={{
                  "data-error-focus": activeErrorKey === focusKey("customerOrderNumber") ? activeErrorKey : undefined,
                }}
                onChange={(event) => {
                  clearFieldErrors(fieldId, actualIndex, "customerOrderNumber");
                  field.onChange(event);
                }}
              />
            )}
          />
        )}
        {metadata.onSale && !showPriceReferenceColumn && !showTargetPriceColumn && <OnSaleIndicator />}
      </Stack>
      <Controller
        control={control}
        name={`rows.${actualIndex}.quantity`}
        render={({ field }) => {
          const error = getError("quantity");
          return (
            <TextField
              {...field}
              disabled={!metadata.quantityVisible}
              error={!!error}
              helperText={error ? t(error) : undefined}
              label={t("cartPage.table.header.quantity.title")}
              type="number"
              inputProps={{
                "data-error-focus": activeErrorKey === focusKey("quantity") ? activeErrorKey : undefined,
                "data-testid": `bulk-upload-quantity-${metadata.originalProductId}`,
                min: 1,
              }}
              onChange={(event) => {
                clearFieldErrors(fieldId, actualIndex, "quantity");
                const inputValue = (event as { target: { value: string } } | undefined)?.target.value ?? "";
                const value = inputValue === "" ? "" : Number(inputValue);
                const previousValue = field.value;
                field.onChange(value);
                onFieldInteraction(fieldId, actualIndex, "quantity", previousValue, value);
              }}
            />
          );
        }}
      />
      <Controller
        control={control}
        name={`rows.${actualIndex}.requestDeliveryDate`}
        render={({ field }) => {
          const error = getError("requestDeliveryDate");
          const isInfo = metadata.messages.some(
            (message) => message.type === "info" && getFieldForMessage(message.code) === "requestDeliveryDate",
          );
          return (
            <DeliveryDateField
              data-error-focus={activeErrorKey === focusKey("requestDeliveryDate") ? activeErrorKey : undefined}
              info={isInfo}
            >
              <MUIDateInput
                date={field.value ? getDate(field.value) : null}
                dataTestId={`bulk-upload-delivery-date-${metadata.originalProductId}`}
                helperLabel={error ? t(error) : undefined}
                isInvalid={!isInfo && !!error}
                label={t("cartPage.table.content.chooseDate")}
                minDate={getFirstValidDate()}
                maxDate={getLastValidDate()}
                onBlur={() => field.onBlur()}
                onChange={(date) => {
                  clearFieldErrors(fieldId, actualIndex, "requestDeliveryDate");
                  const value = formatApiDate(date);
                  const previousValue = field.value;
                  field.onChange(value);
                  onFieldInteraction(fieldId, actualIndex, "requestDeliveryDate", previousValue, value);
                }}
                onClear={() => {
                  clearFieldErrors(fieldId, actualIndex, "requestDeliveryDate");
                  const previousValue = field.value;
                  field.onChange("");
                  onFieldInteraction(fieldId, actualIndex, "requestDeliveryDate", previousValue, "");
                }}
                showClear
              />
            </DeliveryDateField>
          );
        }}
      />
      {showPriceReferenceColumn &&
        (metadata.onSale ? (
          <OnSaleIndicator />
        ) : priceReferenceOptions.length > 0 ? (
          <Controller
            control={control}
            name={`rows.${actualIndex}.priceReference`}
            render={({ field }) => {
              const error = getError("priceReference");
              return (
                <TextField
                  {...field}
                  select
                  error={!!error}
                  helperText={error ? t(error) : undefined}
                  label={t("cartPage.table.header.priceReference.title")}
                  inputProps={{
                    "data-error-focus": activeErrorKey === focusKey("priceReference") ? activeErrorKey : undefined,
                  }}
                  onChange={(event) => {
                    clearFieldErrors(fieldId, actualIndex, "priceReference");
                    const value = event.target.value;
                    const previousValue = field.value;
                    onFieldInteraction(fieldId, actualIndex, "priceReference", previousValue, value);
                    field.onChange(event);
                  }}
                >
                  {priceReferenceOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
        ) : (
          <Box />
        ))}
      {showTargetPriceColumn &&
        (isTargetPriceApplicable(targetPriceEnabled, metadata) ? (
          <Controller
            control={control}
            name={`rows.${actualIndex}.targetPrice`}
            render={({ field }) => {
              const error = getError("targetPrice");
              return (
                <CustomNumberFormat
                  value={field.value === "" ? null : field.value}
                  decimalScale={2}
                  error={!!error}
                  handleState={({ floatValue }) => {
                    clearFieldErrors(fieldId, actualIndex, "targetPrice");
                    const value = floatValue ?? "";
                    const previousValue = field.value;
                    field.onChange(value);
                    onFieldInteraction(fieldId, actualIndex, "targetPrice", previousValue, value);
                  }}
                  helperText={error ? t(error) : undefined}
                  label={t("cartPage.table.header.targetPrice.title")}
                  inputProps={{
                    "data-error-focus": activeErrorKey === focusKey("targetPrice") ? activeErrorKey : undefined,
                    "data-testid": `bulk-upload-target-price-${metadata.originalProductId}`,
                  }}
                  onBlur={() => {
                    field.onBlur();
                    onTargetPriceBlur(actualIndex);
                  }}
                />
              );
            }}
          />
        ) : metadata.onSale && !showPriceReferenceColumn ? (
          <OnSaleIndicator />
        ) : (
          <Box />
        ))}
      <RowActionButtons>
        {canMakeComments && (
          <IconButton
            type="button"
            aria-label={t("cartPage.table.comment")}
            aria-controls={`bulk-upload-comment-${fieldId}`}
            aria-expanded={commentOpen}
            color="primary"
            size="small"
            onClick={() => setCommentOpen((open) => !open)}
          >
            {commentOpen ? <CommentsDisabledOutlinedIcon /> : <CommentOutlinedIcon />}
          </IconButton>
        )}
        <IconButton
          type="button"
          aria-label={t("cart.deleteLine")}
          data-testid={`bulk-upload-remove-${metadata.originalProductId}`}
          color="primary"
          size="small"
          onClick={() => onRemove(fieldId, actualIndex)}
        >
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </RowActionButtons>
      <Controller
        control={control}
        name={`rows.${actualIndex}.comment`}
        render={({ field }) => (
          <>
            {canMakeComments && commentOpen && (
              <CommentField
                {...field}
                id={`bulk-upload-comment-${fieldId}`}
                fullWidth
                label={t("cartPage.table.comment")}
                inputProps={{
                  "data-error-focus": activeErrorKey === focusKey("comment") ? activeErrorKey : undefined,
                }}
                onChange={(event) => {
                  clearFieldErrors(fieldId, actualIndex, "comment");
                  field.onChange(event);
                }}
              />
            )}
          </>
        )}
      />
      {(metadata.isPMA || rowMessages.length > 0) && (
        <RowMessages>
          {metadata.isPMA && <Alert severity="info">{t("product.pma.messages.long")}</Alert>}
          {rowMessages.map((message) => (
            <Alert key={`${message.type}:${message.code}`} severity={message.type}>
              {t(message.code)}
            </Alert>
          ))}
        </RowMessages>
      )}
    </RowCard>
  );
});

function OnSaleIndicator() {
  const { t } = useTranslation();
  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ minHeight: 56 }}>
      <SaleTag size={32} />
      <Typography color="salesCampaign" variant="bodyLarge">
        {t("feature.bulkEntry.tag.onSale")}
      </Typography>
    </Stack>
  );
}

function toFormRow(product: BulkUploadPreviewProduct): BulkUploadFormRow {
  return {
    comment: product.comment,
    customerOrderNumber: product.customerOrderNumber,
    priceReference: product.priceReference,
    productId: product.productId,
    quantity: product.quantity,
    requestDeliveryDate: product.requestDeliveryDate ?? "",
    targetPrice: product.targetPrice ?? "",
  };
}

function toAnalyticsPreviewItem(row: BulkUploadFormRow): GABulkUpload.BulkUploadAnalyticsPreviewItem {
  return {
    priceReference: row.priceReference,
    productId: row.productId,
    quantity: row.quantity === "" ? 0 : row.quantity,
    requestDeliveryDate: row.requestDeliveryDate || undefined,
    targetPrice: row.targetPrice === "" ? undefined : row.targetPrice,
  };
}

function toIntent(
  row: BulkUploadFormRow,
  metadata: RowMetadata,
  targetPriceEnabled: boolean,
): BulkUploadEntryIntentWsDTO {
  return {
    productId: row.productId.trim(),
    quantity: Number(row.quantity),
    ...(row.requestDeliveryDate ? { requestDeliveryDate: row.requestDeliveryDate } : {}),
    ...(row.comment ? { comment: row.comment } : {}),
    ...(row.priceReference ? { priceReference: row.priceReference } : {}),
    ...(row.customerOrderNumber ? { customerOrderNumber: row.customerOrderNumber } : {}),
    ...(isTargetPriceApplicable(targetPriceEnabled, metadata) && row.targetPrice !== ""
      ? { targetPrice: Number(row.targetPrice) }
      : {}),
  };
}

function isTargetPriceApplicable(
  targetPriceEnabled: boolean,
  metadata: Pick<RowMetadata, "housingProduct" | "onSale">,
): boolean {
  return targetPriceEnabled && !metadata.housingProduct && !metadata.onSale;
}

function resolveProductId(product: ProductData | undefined, inputValue: string): string {
  const sapMaterialNumber = product?.sapMaterialNumber;
  if (sapMaterialNumber && inputValue.length === 15 && /\d{2}/.test(inputValue.substring(13))) {
    return `${sapMaterialNumber}${inputValue.substring(13)}`;
  }
  return product?.designation ?? inputValue;
}

function normalizeEditableField(field: string): EditableField {
  const aliases: Record<string, EditableField> = {
    comment: "comment",
    customerOrderNumber: "customerOrderNumber",
    priceReference: "priceReference",
    productId: "productId",
    quantity: "quantity",
    requestDeliveryDate: "requestDeliveryDate",
    targetPrice: "targetPrice",
  };
  return aliases[field] ?? "productId";
}

function inferMessageField(code: string): EditableField {
  if (code.includes("quantity")) {
    return "quantity";
  }
  if (code.includes("rdd") || code.includes("delivery")) {
    return "requestDeliveryDate";
  }
  if (code.includes("reference")) {
    return "priceReference";
  }
  if (code.includes("targetPrice")) {
    return "targetPrice";
  }
  return "productId";
}

function getFieldForMessage(code: string): EditableField | undefined {
  if (code.includes("quantity")) {
    return "quantity";
  }
  if (code.includes("rdd") || code.includes("delivery")) {
    return "requestDeliveryDate";
  }
  if (code.includes("reference")) {
    return "priceReference";
  }
  if (code.includes("targetPrice")) {
    return "targetPrice";
  }
  if (code.includes("product.id") || code.includes("productId")) {
    return "productId";
  }
  return undefined;
}

function dedupeBackendErrors(errors: BackendError[]): BackendError[] {
  const seen = new Set<string>();
  return errors.filter((error) => {
    const key = `${error.fieldId}:${error.field}:${error.code}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function dedupeRenderableMessages(
  messages: { code: string; type: "info" | "error" }[],
): { code: string; type: "info" | "error" }[] {
  const seen = new Set<string>();
  return messages.filter((message) => {
    const key = `${message.type}:${message.code}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function buildErrorStops({
  backendErrors,
  errors,
  visibleFields,
}: {
  backendErrors: BackendError[];
  errors: FieldErrors<BulkUploadForm>;
  visibleFields: {
    actualIndex: number;
    field: BulkUploadFormRow & { fieldId: string };
    metadata: RowMetadata;
  }[];
}): ErrorStop[] {
  const stops: ErrorStop[] = [];
  visibleFields.forEach(({ actualIndex, field }, visibleIndex) => {
    const backendError = backendErrors.find((error) => error.fieldId === field.fieldId);
    if (backendError) {
      stops.push({
        ...backendError,
        actualIndex,
        focusKey: `${field.fieldId}:${backendError.field}:${backendError.code}`,
        key: field.fieldId,
        visibleIndex,
      });
      return;
    }
    const rowErrors = errors.rows?.[actualIndex];
    if (!rowErrors) {
      return;
    }
    for (const fieldName of [
      "productId",
      "quantity",
      "requestDeliveryDate",
      "comment",
      "priceReference",
      "customerOrderNumber",
      "targetPrice",
    ] as const) {
      const error = rowErrors[fieldName];
      if (!error) {
        continue;
      }
      const code = typeof error.message === "string" ? error.message : `bulkUpload.error.${fieldName}.invalid`;
      stops.push({
        actualIndex,
        code,
        field: fieldName,
        fieldId: field.fieldId,
        focusKey: `${field.fieldId}:${fieldName}:${code}`,
        key: field.fieldId,
        visibleIndex,
      });
      break;
    }
  });
  return stops;
}

const EMPTY_BACKEND_ERRORS: BackendError[] = [];
const PREVIEW_GRID_COLUMNS_BOTH = "32px 200px 136px 200px 264px 135px 56px";
const PREVIEW_GRID_COLUMNS_ONE_OPTIONAL = "32px 272px 136px 272px 270px 56px";
const PREVIEW_GRID_COLUMNS_NO_OPTIONAL = "32px 322px 322px 323px 56px";

interface PreviewGridProps {
  $showPriceReferenceColumn: boolean;
  $showTargetPriceColumn: boolean;
}

function getPreviewGridColumns({ $showPriceReferenceColumn, $showTargetPriceColumn }: PreviewGridProps) {
  if ($showPriceReferenceColumn && $showTargetPriceColumn) {
    return PREVIEW_GRID_COLUMNS_BOTH;
  }
  if ($showPriceReferenceColumn || $showTargetPriceColumn) {
    return PREVIEW_GRID_COLUMNS_ONE_OPTIONAL;
  }
  return PREVIEW_GRID_COLUMNS_NO_OPTIONAL;
}

const PreviewForm = styled("form")({
  position: "relative",
});

const StickyMessageSummary = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(2),
  position: "sticky",
  top: 0,
  zIndex: 2,
}));

const ErrorNavigation = styled(Alert)({
  alignItems: "center",
  maxWidth: 760,
  width: "100%",
  "& .MuiAlert-message": {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
});

const MessageNavigation = styled("div")(({ theme }) => ({
  maxWidth: 760,
  width: "100%",
  "& .MuiAlert-root, & .MuiAlert-message": {
    alignItems: "center",
  },
  "& .MuiAlert-message > span": {
    ...theme.typography.bodyMedium,
  },
  "& .MuiIconButton-root": {
    height: 24,
    padding: "2px",
    width: 24,
  },
  "& .MuiIconButton-root .MuiSvgIcon-root": {
    fontSize: 18,
  },
}));

const NavigationButtons = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  flexDirection: "row",
  gap: theme.spacing(1),
  "& .MuiIconButton-root": {
    height: 24,
    padding: "2px",
    width: 24,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
}));

const TableHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "$showPriceReferenceColumn" && prop !== "$showTargetPriceColumn",
})<PreviewGridProps>(({ theme, ...gridProps }) => ({
  alignItems: "center",
  borderBottom: `3px solid ${theme.palette.divider}`,
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: getPreviewGridColumns(gridProps),
  minWidth: "1152px",
  padding: theme.spacing(1, 2),
  "& .MuiTypography-root": {
    fontWeight: "bold",
  },
}));

const VirtualCanvas = styled("div")({
  minWidth: "1152px",
  position: "relative",
  width: "100%",
});

const VirtualRow = styled("div")({
  left: 0,
  position: "absolute",
  top: 0,
  width: "100%",
});

const RowCard = styled("div", {
  shouldForwardProp: (prop) => prop !== "$showPriceReferenceColumn" && prop !== "$showTargetPriceColumn",
})<PreviewGridProps>(({ theme, ...gridProps }) => ({
  alignItems: "start",
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: getPreviewGridColumns(gridProps),
  padding: theme.spacing(2),
}));

const RowMessages = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  gridColumn: "2 / -1",
}));

const RowActionButtons = styled(Stack)({
  alignItems: "flex-start",
  flexDirection: "row",
  justifyContent: "flex-end",
});

const HeaderActions = styled(RowActionButtons)({
  alignItems: "center",
});

const CommentField = styled(TextField)({
  gridColumn: "2 / -1",
});

const DeliveryDateField = styled(Box, {
  shouldForwardProp: (prop) => prop !== "info",
})<{ info: boolean }>(({ info, theme }) =>
  info
    ? {
        "& .MuiFormHelperText-root": {
          color: theme.palette.info.main,
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderColor: theme.palette.info.main,
          },
      }
    : {},
);

const FooterActions = styled(DialogActions)(({ theme }) => ({
  alignItems: "center",
  background: theme.palette.background.paper,
  bottom: 0,
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  margin: theme.spacing(1, -3, -3),
  minHeight: theme.spacing(11),
  padding: theme.spacing(3),
  position: "sticky",
  zIndex: 2,
  "& > :first-of-type": {
    marginRight: "auto",
  },
}));

const LoadingOverlay = styled(Backdrop)(({ theme }) => ({
  alignItems: "center",
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  position: "absolute",
  zIndex: 3,
}));
